import { type UseQueryResult } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { type UserResponse } from '../contracts';

export type QueryUserResponse = UserResponse & {
  hasUnlimitedQuota: boolean;
};

interface CurrentUserProviderProps extends PropsWithChildren {
  userQuery: UseQueryResult<QueryUserResponse, Error>;
}

export type QueryUserRefetch = () => Promise<{ data: QueryUserResponse | undefined }>;

type QueryUserSuccessResult<T> = {
  data: T;
  isLoading: false;
  isPending: false;
  isSuccess: true;
  refetch: QueryUserRefetch;
};

type QueryUserPendingResult = {
  data: undefined;
  isLoading: boolean;
  isPending: boolean;
  isSuccess: false;
  refetch: QueryUserRefetch;
};

export type QueryUserQueryResult<T = QueryUserResponse> =
  | QueryUserSuccessResult<T>
  | QueryUserPendingResult;

export type QueryUserSelector<T> = (data: QueryUserResponse) => T;

const initialCurrentUserQueryResult: QueryUserQueryResult = {
  data: undefined,
  isLoading: false,
  isPending: true,
  isSuccess: false,
  refetch: () => Promise.resolve({ data: undefined }),
};

// Bridge the single React Query user observer into a selector-friendly Jotai store.
const currentUserQueryResultAtom = atom<QueryUserQueryResult>(
  initialCurrentUserQueryResult
);

const shallowEqual = <T,>(previous: T, next: T) => {
  if (Object.is(previous, next)) {
    return true;
  }

  if (
    typeof previous !== 'object' ||
    previous === null ||
    typeof next !== 'object' ||
    next === null
  ) {
    return false;
  }

  const previousRecord = previous as Record<PropertyKey, unknown>;
  const nextRecord = next as Record<PropertyKey, unknown>;
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);

  if (previousKeys.length !== nextKeys.length) {
    return false;
  }

  return previousKeys.every((key) => Object.is(previousRecord[key], nextRecord[key]));
};

const isQueryUserResultEqual = <T,>(
  previous: QueryUserQueryResult<T>,
  next: QueryUserQueryResult<T>
) => {
  return (
    shallowEqual(previous.data, next.data) &&
    previous.isLoading === next.isLoading &&
    previous.isPending === next.isPending &&
    previous.isSuccess === next.isSuccess
  );
};

const toCurrentUserQueryResult = (
  userQuery: UseQueryResult<QueryUserResponse, Error>
): QueryUserQueryResult => {
  if (userQuery.isSuccess) {
    return {
      data: userQuery.data,
      isLoading: false,
      isPending: false,
      isSuccess: true,
      refetch: userQuery.refetch,
    };
  }

  return {
    data: undefined,
    isLoading: userQuery.isLoading,
    isPending: userQuery.isPending,
    isSuccess: false,
    refetch: userQuery.refetch,
  };
};

export const useCurrentUserQueryResult = <T = QueryUserResponse,>(
  select?: QueryUserSelector<T>
): QueryUserQueryResult<T> => {
  const selectRef = useRef(select);
  selectRef.current = select;

  const selectedAtom = useMemo(
    () =>
      selectAtom(
        currentUserQueryResultAtom,
        (query): QueryUserQueryResult<T> => {
          if (query.isSuccess) {
            const selectedData = selectRef.current
              ? selectRef.current(query.data)
              : (query.data as T);

            return {
              data: selectedData,
              isLoading: false,
              isPending: false,
              isSuccess: true,
              refetch: query.refetch,
            };
          }

          return {
            data: undefined,
            isLoading: query.isLoading,
            isPending: query.isPending,
            isSuccess: false,
            refetch: query.refetch,
          };
        },
        isQueryUserResultEqual
      ),
    []
  );

  return useAtomValue(selectedAtom);
};

export const CurrentUserProvider = ({
  children,
  userQuery,
}: CurrentUserProviderProps) => {
  const setCurrentUserQuery = useSetAtom(currentUserQueryResultAtom);

  useEffect(() => {
    setCurrentUserQuery(toCurrentUserQueryResult(userQuery));
  }, [setCurrentUserQuery, userQuery]);

  return children;
};
